import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  RefreshCw,
  TrendingUp,
  Package,
  Truck,
  DollarSign,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const PurchaseReports = () => {
  const { currencySymbol } = useSettings();
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/procurement/stats`),
        axios.get(`${API_URL}/api/procurement/reports/purchase-summary`)
      ]);
      setStats(statsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const poStatusData = summary?.po_by_status ? 
    Object.entries(summary.po_by_status).map(([status, data]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: data.value,
      count: data.count
    })) : [];

  const topSuppliersData = summary?.top_suppliers?.slice(0, 5) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Reports</h1>
          <p className="text-muted-foreground">Overview of procurement activities and analytics.</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.suppliers?.total || 0}</div>
                <div className="text-sm text-muted-foreground">Suppliers</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.products?.total || 0}</div>
                <div className="text-sm text-muted-foreground">Products</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{currencySymbol} {(stats?.purchase_orders?.total_value || 0).toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Total PO Value</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{currencySymbol} {(stats?.bills?.total_payable || 0).toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Accounts Payable</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-blue-700">{stats?.purchase_orders?.total || 0}</div>
            <div className="text-sm text-blue-600">Total POs</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50/50 border-amber-200">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-amber-700">{stats?.purchase_orders?.pending || 0}</div>
            <div className="text-sm text-amber-600">Pending POs</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50/50 border-red-200">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-red-700">{stats?.bills?.unpaid || 0}</div>
            <div className="text-sm text-red-600">Unpaid Bills</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50/50 border-emerald-200">
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-emerald-700">{currencySymbol} {(stats?.payments?.total_paid || 0).toLocaleString()}</div>
            <div className="text-sm text-emerald-600">Total Paid</div>
          </CardContent>
        </Card>
        <Card className={stats?.products?.low_stock > 0 ? "bg-orange-50/50 border-orange-300" : ""}>
          <CardContent className="pt-6 text-center">
            <div className={`text-3xl font-bold ${stats?.products?.low_stock > 0 ? 'text-orange-700' : ''}`}>
              {stats?.products?.low_stock || 0}
            </div>
            <div className={`text-sm ${stats?.products?.low_stock > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
              Low Stock Items
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* PO by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Purchase Orders by Status</CardTitle>
            <CardDescription>Value distribution across PO statuses</CardDescription>
          </CardHeader>
          <CardContent>
            {poStatusData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={poStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {poStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${currencySymbol} ${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No purchase order data</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Suppliers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Suppliers by Purchase Value</CardTitle>
            <CardDescription>Highest spending suppliers</CardDescription>
          </CardHeader>
          <CardContent>
            {topSuppliersData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topSuppliersData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${currencySymbol} ${(v/1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => `${currencySymbol} ${value.toLocaleString()}`} />
                    <Bar dataKey="total" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No supplier data</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inventory Value */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5" /> Inventory Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-3xl font-bold">{stats?.products?.total || 0}</div>
              <div className="text-sm text-muted-foreground">Total Products</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-3xl font-bold text-emerald-700">
                {currencySymbol} {(stats?.products?.stock_value || 0).toLocaleString()}
              </div>
              <div className="text-sm text-emerald-600">Total Stock Value</div>
            </div>
            <div className={`text-center p-4 rounded-lg ${stats?.products?.low_stock > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-muted/50'}`}>
              <div className={`text-3xl font-bold ${stats?.products?.low_stock > 0 ? 'text-orange-700' : ''}`}>
                {stats?.products?.low_stock || 0}
              </div>
              <div className={`text-sm flex items-center justify-center gap-1 ${stats?.products?.low_stock > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                {stats?.products?.low_stock > 0 && <AlertTriangle className="h-4 w-4" />}
                Low Stock Alerts
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseReports;
